import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { getPublicStats } from "../lib/publicStats";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import {
  Compass,
  X,
  MagnifyingGlass,
  Storefront,
  Handshake,
  Crown,
  PaintBrushHousehold,
  ChatCircleText,
  Sparkle,
  ArrowRight,
  MapPin,
  FilePdf,
  ChartLine,
  SignIn,
} from "@phosphor-icons/react";

const STORAGE_KEY = "mc68_guide_dismissed";
const STORAGE_COMPLETED = "mc68_guide_completed";

/**
 * End-to-end user guide.
 * - Floating "Comment ça marche ?" launcher (bottom-right) — dismissible.
 * - Modal with two tracks: "Je cherche un commerce" (B2C) and "Je suis commerçant" (B2B).
 * - Each step has: icon, title, plain-language description, and a direct CTA to the right page.
 * - Persists dismissal and completion in localStorage. Re-openable from Footer (see Footer link).
 */
export default function OnboardingGuide() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [cookieDecided, setCookieDecided] = useState(false);
  const [track, setTrack] = useState("client"); // "client" | "pro"
  const [step, setStep] = useState(0);
  const [bizCount, setBizCount] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang, t } = useI18n();

  useEffect(() => {
    getPublicStats().then((r) => setBizCount(r.data?.businesses)).catch(() => {});
  }, []);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    setHidden(!!dismissed);
    const checkCookies = () => setCookieDecided(!!localStorage.getItem("mc68_cookies"));
    checkCookies();
    // Poll briefly for cookie decision (banner uses localStorage)
    const interval = setInterval(checkCookies, 600);
    // Listen for manual open requests (Footer / button outside)
    const handler = (e) => {
      setTrack(e.detail?.track || "client");
      setStep(0);
      setOpen(true);
      setHidden(false);
    };
    window.addEventListener("mc68:open-guide", handler);
    return () => {
      window.removeEventListener("mc68:open-guide", handler);
      clearInterval(interval);
    };
  }, []);

  const close = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, "1");
    setHidden(true);
  };

  const complete = async () => {
    localStorage.setItem(STORAGE_COMPLETED, new Date().toISOString());
    const stepsTotal = TRACKS[track]?.steps?.length || 0;
    // Track completion on the backend (authenticated users only).
    if (user) {
      try {
        const r = await api.post("/dashboard/guide/completion", {
          track,
          steps_total: stepsTotal,
          language: lang,
        });
        if (track === "pro" && r.data?.welcome_email_sent) {
          toast.success("Email de bienvenue envoyé à " + user.email);
        }
      } catch (e) {
        // Silent fail — not critical for UX.
        console.debug("guide completion not tracked", e?.message);
      }
    }
    close();
  };

  const TRACKS = {
    client: {
      label: "Je cherche un commerce",
      sub: "Trouver la bonne adresse en 30 secondes",
      icon: <MagnifyingGlass size={18} weight="duotone" />,
      steps: [
        {
          icon: <MagnifyingGlass size={26} weight="duotone" />,
          title: "1. Tapez votre besoin",
          desc: "Dans la barre de recherche en haut du site, tapez le nom d'une entreprise, un métier (« boulangerie », « plombier »), ou une ville. L'autocomplétion vous guide en direct.",
          cta: { label: "Essayer la recherche", to: "/" },
        },
        {
          icon: <MapPin size={26} weight="duotone" />,
          title: "2. Explorez l'annuaire ou la carte",
          desc: "Filtrez par ville, catégorie ou mot-clé. Basculez entre vue liste et carte. Les partenaires Premium apparaissent en tête avec un badge doré.",
          cta: { label: "Ouvrir l'annuaire", to: "/annuaire" },
        },
        {
          icon: <Sparkle size={26} weight="duotone" />,
          title: "3. Testez la recherche intelligente",
          desc: "Tapez une question naturelle — par ex. « plombier en urgence à Mulhouse » ou « boulangerie ouverte dimanche ». L'IA comprend et classe les 5 meilleures fiches.",
          cta: { label: "Recherche IA", to: "/recherche" },
        },
        {
          icon: <Storefront size={26} weight="duotone" />,
          title: "4. Consultez la fiche",
          desc: "Photos, horaires, description, avis clients modérés, coordonnées : tout est regroupé. Un clic pour appeler, envoyer un WhatsApp, demander un devis.",
          cta: null,
        },
        {
          icon: <ChatCircleText size={26} weight="duotone" />,
          title: "5. Contactez ou laissez un avis",
          desc: "Envoyez votre demande directement depuis la fiche. Après votre visite, laissez un avis — il sera modéré puis publié pour aider les autres habitants.",
          cta: null,
        },
      ],
    },
    pro: {
      label: "Je suis commerçant",
      sub: "Passer de A à Z en 10 minutes",
      icon: <Storefront size={18} weight="duotone" />,
      steps: [
        {
          icon: <MagnifyingGlass size={26} weight="duotone" />,
          title: "1. Trouvez votre fiche",
          desc: `Votre entreprise est probablement déjà référencée — nous avons importé plus de ${(bizCount ?? 21000).toLocaleString("fr-FR")} commerces du Haut-Rhin depuis les données publiques INSEE. Cherchez son nom dans la barre en haut.`,
          cta: { label: "Chercher mon entreprise", to: "/annuaire" },
        },
        {
          icon: <SignIn size={26} weight="duotone" />,
          title: "2. Créez votre compte",
          desc: "Connectez-vous avec Google, ou demandez un lien magique par email (sans mot de passe). 30 secondes, pas de carte bancaire à cette étape.",
          cta: { label: "Se connecter", to: "/connexion" },
        },
        {
          icon: <Handshake size={26} weight="duotone" />,
          title: "3. Réclamez votre fiche",
          desc: "Sur votre fiche, cliquez « C'est mon entreprise ». Elle est immédiatement reliée à votre compte — vous pouvez la modifier.",
          cta: null,
        },
        {
          icon: <PaintBrushHousehold size={26} weight="duotone" />,
          title: "4. Personnalisez",
          desc: "Depuis votre espace, complétez description, horaires, téléphone, WhatsApp, site web. Ajoutez un logo et jusqu'à 8 photos (glisser-déposer).",
          cta: { label: "Ouvrir mon espace", to: "/dashboard" },
        },
        {
          icon: <Crown size={26} weight="duotone" />,
          title: "5. Passez Premium (optionnel)",
          desc: "14,99 € / mois (Premium) ou 29,99 € / mois (Premium IA) : mise en avant prioritaire, outils IA, analytics, flyer PDF à imprimer. Sans engagement, résiliable en un clic.",
          cta: { label: "Voir les offres", to: "/passez-partenaire" },
        },
        {
          icon: <FilePdf size={26} weight="duotone" />,
          title: "6. Téléchargez votre flyer",
          desc: "Un flyer A5 prêt à imprimer avec QR code vers votre fiche, idéal pour votre vitrine ou votre comptoir. Quota : 3 téléchargements par mois.",
          cta: { label: "Mon espace partenaire", to: "/dashboard" },
        },
        {
          icon: <ChartLine size={26} weight="duotone" />,
          title: "7. Suivez vos résultats",
          desc: "Vues, clics téléphone, clics WhatsApp, demandes clients : tout est lisible sur votre tableau de bord. L'IA vous propose des conseils pour progresser.",
          cta: { label: "Voir mes statistiques", to: "/dashboard" },
        },
      ],
    },
  };

  const active = TRACKS[track];
  const steps = active.steps;
  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <>
      {/* Floating launcher — hidden while cookie banner is showing (before decision) */}
      {!open && !hidden && cookieDecided && (
        <button
          onClick={() => { setOpen(true); setStep(0); }}
          className="fixed bottom-20 right-5 sm:bottom-5 sm:right-24 z-40 bg-primary text-primary-foreground hover:bg-[#0f3d29] shadow-[0_20px_48px_-16px_rgba(19,78,52,0.5)] rounded-full pl-4 pr-5 py-3 flex items-center gap-2 text-sm font-semibold transition-all hover:-translate-y-0.5 group"
          data-testid="onboarding-launcher"
          aria-label="Ouvrir le guide"
        >
          <span className="w-7 h-7 rounded-full bg-secondary text-secondary-foreground grid place-items-center">
            <Compass size={16} weight="fill" />
          </span>
          {t.widgets?.onboarding || "Comment ça marche ?"}
          <span className="hidden md:inline text-primary-foreground/70 text-xs">· 2 min</span>
        </button>
      )}

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-foreground/30 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close}
            data-testid="onboarding-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
              className="relative w-full max-w-2xl bg-card border border-border rounded-sm shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top bar */}
              <div className="relative border-b border-border bg-accent/30">
                <div className="aurora opacity-50" />
                <div className="relative flex items-center justify-between px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-primary text-primary-foreground grid place-items-center">
                      <Compass size={20} weight="fill" />
                    </div>
                    <div>
                      <div className="font-display text-lg font-semibold leading-tight">Guide Mon Commerce 68</div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">De A à Z en quelques minutes</div>
                    </div>
                  </div>
                  <button onClick={close} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid="onboarding-close" aria-label="Fermer">
                    <X size={18} weight="bold" />
                  </button>
                </div>

                {/* Track tabs */}
                <div className="flex gap-2 px-6 pb-4">
                  {Object.entries(TRACKS).map(([key, tr]) => (
                    <button
                      key={key}
                      onClick={() => { setTrack(key); setStep(0); }}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-sm text-xs font-semibold transition-all ${track === key ? "bg-primary text-primary-foreground shadow-sm" : "bg-background border border-border text-muted-foreground hover:text-foreground"}`}
                      data-testid={`onboarding-track-${key}`}
                    >
                      {tr.icon} {tr.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step body */}
              <div className="px-6 pt-6 pb-5">
                <div className="label-uppercase mb-3 !text-[0.65rem]">{active.sub} · Étape {step + 1} / {steps.length}</div>
                <div className="flex gap-5 items-start">
                  <div className="w-14 h-14 shrink-0 rounded-sm bg-primary/10 text-primary grid place-items-center">
                    {current.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl font-medium mb-2">{current.title}</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{current.desc}</p>
                    {current.cta && (
                      <button
                        onClick={() => { navigate(current.cta.to); close(); }}
                        className="mt-4 inline-flex items-center gap-2 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2 text-xs font-semibold rounded-sm transition-colors"
                        data-testid={`onboarding-step-cta-${step}`}
                      >
                        {current.cta.label} <ArrowRight size={12} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress dots */}
                <div className="flex gap-1.5 mt-6">
                  {steps.map((s, i) => (
                    <button
                      key={s.id || s.title || `step-${i}`}
                      onClick={() => setStep(i)}
                      className={`h-1.5 rounded-full transition-all ${i === step ? "bg-primary w-8" : i < step ? "bg-primary/50 w-4" : "bg-border w-4"}`}
                      data-testid={`onboarding-progress-${i}`}
                      aria-label={`Étape ${i + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Footer actions */}
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-background">
                <button
                  onClick={close}
                  className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="onboarding-skip"
                >
                  Passer le guide
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="px-4 py-2 text-xs font-semibold border border-border rounded-sm hover:border-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="onboarding-prev"
                  >
                    Précédent
                  </button>
                  {isLast ? (
                    <button
                      onClick={complete}
                      className="px-4 py-2 text-xs font-semibold bg-primary text-primary-foreground hover:bg-[#0f3d29] rounded-sm flex items-center gap-2 transition-colors"
                      data-testid="onboarding-finish"
                    >
                      <Sparkle size={12} weight="fill" /> J'ai compris
                    </button>
                  ) : (
                    <button
                      onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                      className="px-4 py-2 text-xs font-semibold bg-primary text-primary-foreground hover:bg-[#0f3d29] rounded-sm flex items-center gap-2 transition-colors"
                      data-testid="onboarding-next"
                    >
                      Suivant <ArrowRight size={12} />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** Small helper button to re-open the guide from anywhere (e.g. Footer). */
export function OpenGuideLink({ track = "client", className = "", children = "Voir le guide" }) {
  const onClick = () => {
    window.dispatchEvent(new CustomEvent("mc68:open-guide", { detail: { track } }));
  };
  return (
    <button onClick={onClick} className={className} data-testid={`open-guide-${track}`}>
      {children}
    </button>
  );
}
